import {
  SONNER_TOASTER_SELECTOR,
  SONNER_VISIBLE_TOAST_SELECTOR,
} from "@/constants/toast";

import type {
  IIsPointInsideAnyToastProps,
  IIsToastOriginatedOutsideEventProps,
  IToastGuardedOutsideHandlers,
  IToastGuardedOutsideHandlersProps,
  IWithToastOutsideGuardProps,
  TOutsideInteractionEvent,
} from "./types";

const isPointInsideAnyToast = ({
  originalEvent,
}: IIsPointInsideAnyToastProps): boolean => {
  if (!(originalEvent instanceof MouseEvent)) return false;
  const { clientX, clientY } = originalEvent;
  return Array.from(
    document.querySelectorAll(SONNER_VISIBLE_TOAST_SELECTOR),
  ).some((toastElement) => {
    const rect = toastElement.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  });
};

// NOTE: Toasts render in sonner's own layer outside every dialog and sheet, and they usually appear while one is open (a save inside a modal
// answers with a toast). Radix treats a pointerdown or focus there as an
// interaction outside the layer and dismisses it. An event is toast-originated
// when its target sits in the Toaster (a toast's buttons or scroll area) or,
// since a toast's text is click-through and the event then targets the
// overlay, when the pointer lies inside a visible toast's box. Radix may ask
// twice for one pointerdown (pointer-down and interact handlers); the check is
// a few DOM reads, so it is not cached.
const isToastOriginatedOutsideEvent = ({
  event,
}: IIsToastOriginatedOutsideEventProps): boolean => {
  const { target } = event;
  if (target instanceof Element && target.closest(SONNER_TOASTER_SELECTOR)) {
    return true;
  }
  return isPointInsideAnyToast({ originalEvent: event.detail.originalEvent });
};

// NOTE: Wraps a Radix outside-interaction handler so a toast-originated event
// is cancelled (the layer stays open) and never reaches the consumer, whose
// handler may close the layer itself; every other event is forwarded.
const withToastOutsideGuard =
  <TEvent extends TOutsideInteractionEvent>({
    handler,
  }: IWithToastOutsideGuardProps<TEvent>): ((event: TEvent) => void) =>
  (event: TEvent): void => {
    if (isToastOriginatedOutsideEvent({ event })) {
      event.preventDefault();
      return;
    }
    handler?.(event);
  };

// NOTE: The handler pair every modal layer (Dialog, Sheet) spreads
// onto its Radix content. onInteractOutside runs for both pointer-down and
// focus outside, so it always carries the guard; a consumer's
// onPointerDownOutside runs first and is guarded too, so it cannot close the
// layer on a toast click itself. A consumer's own preventDefault there still
// lets its onInteractOutside run, as without the guard.
export const getToastGuardedOutsideHandlers = <
  TPointerDownEvent extends TOutsideInteractionEvent,
  TInteractEvent extends TOutsideInteractionEvent,
>({
  onPointerDownOutside,
  onInteractOutside,
}: IToastGuardedOutsideHandlersProps<
  TPointerDownEvent,
  TInteractEvent
>): IToastGuardedOutsideHandlers<TPointerDownEvent, TInteractEvent> => ({
  onPointerDownOutside:
    onPointerDownOutside &&
    withToastOutsideGuard({ handler: onPointerDownOutside }),
  onInteractOutside: withToastOutsideGuard({ handler: onInteractOutside }),
});
